import { useCallback, useEffect, useMemo, useState } from 'react'
import { EmptyState } from '../components/EmptyState'
import { InlineNotice } from '../components/InlineNotice'
import { LoadingBlock } from '../components/LoadingBlock'
import { Modal } from '../components/Modal'
import { useLanguage } from '../contexts/LanguageContext'
import { errorMessage } from '../lib/errors'
import { formatDate, formatMoney } from '../lib/format'
import { consumePendingAction } from '../lib/pendingAction'
import { supabase } from '../lib/supabase'

type ExpenseRow = {
  id: string
  category: string
  supplier: string | null
  expense_date: string
  amount_net: number
  amount_gross: number
  vat_rate: number
  notes: string | null
  order_id: string | null
  orders?: { id: string; number: string } | null
}

type OrderOption = { id: string; number: string }

type ExpenseForm = {
  category: string
  supplier: string
  expense_date: string
  amount_net: string
  vat_rate: string
  order_id: string
  notes: string
}

const categories = [
  'flowers', 'greenery', 'packaging', 'decorations', 'transport',
  'delivery', 'rental', 'helpers', 'other'
] as const

const categoryLabels = {
  pl: {
    flowers: 'Kwiaty', greenery: 'Zieleń', packaging: 'Opakowania', decorations: 'Dekoracje',
    transport: 'Transport', delivery: 'Dostawa', rental: 'Wynajem', helpers: 'Pomocnicy', other: 'Inne'
  },
  ru: {
    flowers: 'Цветы', greenery: 'Зелень', packaging: 'Упаковка', decorations: 'Декор',
    transport: 'Транспорт', delivery: 'Доставка', rental: 'Аренда', helpers: 'Помощники', other: 'Другое'
  }
} as const

const today = () => new Date().toISOString().slice(0, 10)
const emptyForm = (): ExpenseForm => ({
  category: 'flowers', supplier: '', expense_date: today(), amount_net: '', vat_rate: '23', order_id: '', notes: ''
})

export function ExpensesPage() {
  const { t, language } = useLanguage()
  const [expenses, setExpenses] = useState<ExpenseRow[]>([])
  const [orders, setOrders] = useState<OrderOption[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [modalOpen, setModalOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState<ExpenseForm>(emptyForm())

  const load = useCallback(async () => {
    if (!supabase) return
    setLoading(true)
    setError(null)
    const [expenseResult, orderResult] = await Promise.all([
      supabase.from('expenses').select('*, orders(id,number)').order('expense_date', { ascending: false }).order('created_at', { ascending: false }),
      supabase.from('orders').select('id,number').neq('confirmation_status', 'cancelled').order('created_at', { ascending: false })
    ])
    const firstError = expenseResult.error || orderResult.error
    if (firstError) setError(firstError.message)
    else {
      setExpenses((expenseResult.data || []) as unknown as ExpenseRow[])
      setOrders((orderResult.data || []) as OrderOption[])
    }
    setLoading(false)
  }, [])

  useEffect(() => { void load() }, [load])
  useEffect(() => {
    const open = () => openCreate()
    if (consumePendingAction('new-expense')) openCreate()
    window.addEventListener('sgflow:new-expense', open)
    return () => window.removeEventListener('sgflow:new-expense', open)
  }, [])

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) return expenses
    return expenses.filter((expense) => [expense.supplier, expense.category, expense.orders?.number, expense.notes]
      .filter(Boolean).join(' ').toLowerCase().includes(query))
  }, [expenses, search])

  function openCreate() {
    setForm(emptyForm())
    setError(null)
    setNotice(null)
    setModalOpen(true)
  }

  function update<K extends keyof ExpenseForm>(key: K, value: ExpenseForm[K]) {
    setForm((current) => ({ ...current, [key]: value }))
  }

  async function save() {
    if (!supabase) return
    if (!form.category || !form.expense_date || !form.amount_net) {
      setError(t('requiredFields'))
      return
    }
    setSaving(true)
    setError(null)
    try {
      const { error: rpcError } = await supabase.rpc('create_expense_v2', {
        p_payload: {
          category: form.category,
          supplier: form.supplier.trim() || null,
          expense_date: form.expense_date,
          amount_net: Number(form.amount_net.replace(',', '.')),
          vat_rate: Number(form.vat_rate.replace(',', '.')),
          order_id: form.order_id || null,
          notes: form.notes.trim() || null
        }
      })
      if (rpcError) throw rpcError
      setModalOpen(false)
      setNotice(t('expenseCreated'))
      await load()
    } catch (caught) {
      setError(errorMessage(caught, t('errorSaving')))
    }
    setSaving(false)
  }

  return <div className="module-stack">
    <section className="module-toolbar panel toolbar-wrap">
      <div className="toolbar-search"><span>⌕</span><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder={t('search')} /></div>
      <button className="secondary-button" onClick={() => void load()}>{t('refresh')}</button>
      <button className="primary-button" onClick={openCreate}>＋ {t('createExpense')}</button>
    </section>
    {notice && <InlineNotice type="success">{notice}</InlineNotice>}
    {error && !modalOpen && <InlineNotice type="error">{error}</InlineNotice>}
    {loading ? <LoadingBlock /> : filtered.length === 0 ? <EmptyState title={t('expensesEmpty')} description={t('expensesEmptyDescription')} action={<button className="primary-button" onClick={openCreate}>{t('createExpense')}</button>} /> : <section className="data-card panel"><div className="table-scroll"><table className="data-table">
      <thead><tr><th>{t('date')}</th><th>{t('expenseCategory')}</th><th>{t('supplier')}</th><th>{t('relatedOrder')}</th><th>{t('amountNet')}</th><th>{t('vatRate')}</th><th>{t('amountGross')}</th></tr></thead>
      <tbody>{filtered.map((expense) => <tr key={expense.id}>
        <td>{formatDate(expense.expense_date, language)}</td>
        <td><span className="record-title">{categoryLabels[language][expense.category as keyof typeof categoryLabels.pl] || expense.category}</span></td>
        <td>{expense.supplier || '—'}</td>
        <td>{expense.orders?.number || '—'}</td>
        <td>{formatMoney(Number(expense.amount_net || 0), language)}</td>
        <td>{Number(expense.vat_rate || 0)}%</td>
        <td>{formatMoney(Number(expense.amount_gross || 0), language)}</td>
      </tr>)}</tbody>
    </table></div></section>}

    <Modal open={modalOpen} title={t('createExpense')} subtitle={t('requiredFields')} onClose={() => setModalOpen(false)} footer={<><button className="secondary-button" onClick={() => setModalOpen(false)}>{t('cancel')}</button><button className="primary-button" disabled={saving} onClick={() => void save()}>{saving ? t('saving') : t('save')}</button></>}>
      {error && <InlineNotice type="error">{error}</InlineNotice>}
      <div className="form-grid form-grid-2">
        <label className="field"><span>{t('expenseCategory')} *</span><select value={form.category} onChange={(event) => update('category', event.target.value)}>{categories.map((category) => <option key={category} value={category}>{categoryLabels[language][category]}</option>)}</select></label>
        <label className="field"><span>{t('expenseDate')} *</span><input type="date" value={form.expense_date} onChange={(event) => update('expense_date', event.target.value)} /></label>
        <label className="field"><span>{t('supplier')}</span><input value={form.supplier} onChange={(event) => update('supplier', event.target.value)} /></label>
        <label className="field"><span>{t('relatedOrder')}</span><select value={form.order_id} onChange={(event) => update('order_id', event.target.value)}><option value="">—</option>{orders.map((order) => <option key={order.id} value={order.id}>{order.number}</option>)}</select></label>
        <label className="field"><span>{t('amountNet')} *</span><input inputMode="decimal" value={form.amount_net} onChange={(event) => update('amount_net', event.target.value)} /></label>
        <label className="field"><span>{t('vatRate')}</span><input inputMode="decimal" value={form.vat_rate} onChange={(event) => update('vat_rate', event.target.value)} /></label>
        <label className="field field-span-2"><span>{t('notes')}</span><textarea rows={4} value={form.notes} onChange={(event) => update('notes', event.target.value)} /></label>
      </div>
    </Modal>
  </div>
}
